import { FunctionComponent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../features/helpers";
import { getAssetsList, getNetworks } from "../../store/AssetsSlice";
import { fetchUserWallets, getClientList, getDepositorsByOrganization, getDepositorsList, getUserInfo, setUserInfo, userInfoResponse } from "../../store/UserSlice";
import { addDeposit } from "../../store/TransactionSlice";
import { getStorageData } from "../../utils/format";
import { Eye, EyeClosed, Banknote } from "lucide-react";
import { fetchDepositAccounts } from "../../store/DepositAccountSlice";
import { fetchDepositsByType, setAddDepoositModalStatus } from "../../store/DepositsSlice";
import { Asset } from "../../models/Assets";
import { depositTypeForRole } from "../../models/Depoist";

import { Modal, FormField, Alert } from "../ui";

export type AddDepositInfo = {
    target_user_id: number;
    userId?: number | null;
    date: Date | null;

    value: number;
    bankId: number;
    accountId?: number;
    type?: string;          // PIX / TED (do account selecionado)
    assetId: number;
    network?: string;
    hash?: string;         // hash on-chain (somente CRYPTO)

    organizationId: number;

    name: string;
    depositor: number;
    document?: string;
    login?: string;
    email?: string;
    password: string;
};

const depositInfoReset: AddDepositInfo = {
    target_user_id: 0, date: null, userId: null,
    value: 0, bankId: 0, accountId: 0, type: "", assetId: 0, network: "", hash: "",
    organizationId: 0, depositor: 0, name: "",
    document: "", login: "", email: "", password: "",
};

const AdminModalDepost: FunctionComponent = () => {

    const dispatch = useAppDispatch();

    const assets: Asset[] = useAppSelector(getAssetsList);
    const networks        = useAppSelector(getNetworks);
    const clients         = useAppSelector(getClientList);
    const depositors      = useAppSelector(getDepositorsList);
    const modalStatus     = useAppSelector((state) => state.deposit.addDepositModalStatus);
    const depositAccounts = useAppSelector((state) => state.depositAccount.accounts);
    const sendLoader      = useAppSelector((state) => state.transaction.addDepositLoading);
    const userInfo        = useAppSelector(getUserInfo);

    const [filteredDepositor, setFilteredDepositor] = useState<Array<userInfoResponse>>([]);

    const [showDepositorPassword, setShowDepositorPassword] = useState(false);
    const [selectedDepositor, setSelectedDepositor] = useState<string>("");
    const [errorMessage, setErrorMessage]           = useState<string>("");
    const [addPayload, setAddPayload]               = useState<AddDepositInfo>(depositInfoReset);
    const [isCrypto, setIsCrypto]                   = useState<boolean>(false);

    useEffect(() => {
        const userInfo = getStorageData();
        dispatch(setUserInfo(userInfo));
        dispatch(getDepositorsByOrganization());
        dispatch(fetchDepositAccounts());
    }, []);

    // Contas de deposito FIAT (PIX/TED) cadastradas pela organizacao.
    const fiatAccounts = (depositAccounts ?? []).filter((a: any) => a.type === "FIAT");
    // Bancos distintos com conta FIAT (p/ o dropdown de banco).
    const fiatBanks = Array.from(
        new Map(fiatAccounts.filter((a: any) => a.bank).map((a: any) => [a.bank.id, a.bank])).values(),
    ) as any[];
    // Contas do banco selecionado.
    const bankAccounts = fiatAccounts.filter(
        (a: any) => (a.bank?.id ?? a.bank_id) === addPayload.bankId,
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setAddPayload((prev) => ({ ...prev, [name]: value }));
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (/^[\d.-]*$/.test(value)) {
            setAddPayload((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (!value) {
            setAddPayload((prev) => ({ ...prev, [name]: "" }));
            return;
        }
        const info = JSON.parse(value);
        setAddPayload((prev) => ({ ...prev, [name]: info.id }));
    };

    const handleBankAccount = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        // trocar de banco zera a conta selecionada
        setAddPayload((prev) => ({ ...prev, bankId: value ? Number(value) : 0, accountId: 0, type: "" }));
    };

    const handleAccountSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = Number(e.target.value);
        const acc = bankAccounts.find((a: any) => a.id === id);
        setAddPayload((prev) => ({
            ...prev,
            accountId: id || 0,
            type:      acc?.transfer_type ?? prev.type,
            bankId:    acc?.bank?.id ?? acc?.bank_id ?? prev.bankId,
        }));
    };

    const handleChangeToken = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        if (!value) {
            setAddPayload((prev) => ({ ...prev, assetId: 0 }));
            return;
        }
        const parsed = JSON.parse(value);
        const crypto = !(parsed.type === "FIAT" || parsed.type === "CONVENTIONAL");
        setIsCrypto(crypto);
        if (crypto) dispatch(fetchUserWallets(addPayload.target_user_id));
        setAddPayload((prev) => ({
            ...prev,
            assetId: parsed.id,
            // CRYPTO nao tem banco/conta; FIAT nao tem hash.
            ...(crypto ? { type: "CRYPTO", bankId: 0, accountId: 0 } : { hash: "" }),
        }));
    };

    const handleDepositorName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSelectedDepositor(value);
        setAddPayload((prev) => ({ ...prev, [name]: value }));
        const filtered = depositors.filter((d: userInfoResponse) =>
            d && (
                d?.nome?.toLowerCase()?.includes(value.toLowerCase()) ||
                d?.email?.toLowerCase()?.includes(value.toLowerCase()) ||
                d?.login?.toLowerCase()?.includes(value.toLowerCase())
            )
        );
        setFilteredDepositor(filtered);
    };

    const clickedDepositor = (d: any) => {
        setSelectedDepositor(d.name);
        setAddPayload((prev) => ({
            ...prev,
            depositor: d.id, name: d.name, document: d.document, email: d.email,
        }));
        setFilteredDepositor([]);
    };

    const validateData = () => true;

    // Fechar o modal (botao X, backdrop ou apos salvar) recarrega a lista de
    // depositos, para a tela refletir o que acabou de ser criado.
    const closeModal = () => {
        dispatch(setAddDepoositModalStatus(false));
        dispatch(fetchDepositsByType(depositTypeForRole(userInfo?.role)));

        // reseta o form pra próxima abertura não vir com dados da última tentativa
        setAddPayload(depositInfoReset);
        setSelectedDepositor("");
        setErrorMessage("");
    };

    const submit = async () => {
        const ok = validateData();
        if (!ok) {
            setErrorMessage("Dados faltando para adicionar um deposito");
            return;
        }

        // aguarda o POST para que o refresh do closeModal ja traga o novo deposito
        await dispatch(addDeposit(addPayload));
        closeModal();
    };

    return (
        <Modal
            open={modalStatus}
            onClose={closeModal}
            icon={Banknote}
            title="Adicionar Deposito"
            subtitle="Adicione todos os dados de um deposito feito"
            maxWidth={800}
            footer={
                <button className="btn-pill primary" onClick={submit}>
                    {sendLoader ? <div className="loader" /> : "Adicionar"}
                </button>
            }
        >
            {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

            <div className="ui-form-section">
                <span className="ui-form-section-title">Cliente</span>

                <FormField label="Cliente" htmlFor="target_user_id">
                    <select id="target_user_id" name="target_user_id" onChange={handleClientChange}>
                        <option value="">Selecione o Cliente</option>
                        {clients?.map((c, i) => (
                            <option key={i} value={JSON.stringify({ id: c.id, name: c.name })}>{c.name}</option>
                        ))}
                    </select>
                </FormField>

                <FormField label="Moeda" htmlFor="asset_id">
                    <select id="asset_id" name="asset_id" onChange={handleChangeToken}>
                        <option value="">Selecione a Moeda</option>
                        {assets?.length > 0 && assets.map((a, i) => (
                            <option key={i} value={JSON.stringify(a)}>{a.name}</option>
                        ))}
                    </select>
                </FormField>

                <FormField label="Valor" htmlFor="value">
                    <input id="value" name="value" type="text" placeholder="Ex: 1234.56" onChange={handleValueChange} />
                </FormField>

                {!isCrypto && (
                    <>
                        <FormField label="Banco" htmlFor="bankId">
                            <select id="bankId" name="bankId" value={addPayload.bankId || ""} onChange={handleBankAccount}>
                                <option value="">Selecione o Banco</option>
                                {fiatBanks.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </FormField>

                        <FormField label="Conta" htmlFor="accountId">
                            <select id="accountId" name="accountId" value={addPayload.accountId || ""} onChange={handleAccountSelect} disabled={!addPayload.bankId}>
                                <option value="">{addPayload.bankId ? "Selecione a Conta" : "Selecione o banco primeiro"}</option>
                                {bankAccounts.map((a: any) => (
                                    <option key={a.id} value={a.id}>
                                        {a.transfer_type ?? "FIAT"} - {a.account} / {a.agency}{a.nickname ? ` (${a.nickname})` : ""}
                                    </option>
                                ))}
                            </select>
                        </FormField>
                    </>
                )}

                {isCrypto && (
                    <FormField label="Hash da transação" htmlFor="hash">
                        <input
                            id="hash" name="hash" type="text" autoComplete="off"
                            placeholder="Hash da transação on-chain"
                            value={addPayload.hash ?? ""} onChange={handleChange}
                        />
                    </FormField>
                )}
            </div>

            <div className="ui-form-section">
                <span className="ui-form-section-title">Depositante</span>

                <FormField label="Nome do depositante" htmlFor="name">
                    <div className="depositor-autocomplete">
                        <input
                            id="name" name="name" type="text" autoComplete="off"
                            placeholder="nome do depositante"
                            value={selectedDepositor}
                            onChange={handleDepositorName}
                        />
                        {filteredDepositor.length > 0 && (
                            <ul>
                                {filteredDepositor.map((d) => (
                                    <li key={d.id} onClick={() => clickedDepositor(d)}>
                                        {d.nome} — <b>{d.login}</b> ({d.email})
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </FormField>

                {!addPayload.depositor && (
                    <>
                        <FormField label="Document" htmlFor="document">
                            <input id="document" name="document" type="text"
                                placeholder="CPF, cnpj or passport"
                                value={addPayload.document} onChange={handleChange} />
                        </FormField>

                        <FormField label="E-mail" htmlFor="email">
                            <input id="email" name="email" type="text" placeholder="e-mail"
                                value={addPayload.email} onChange={handleChange} />
                        </FormField>

                        <div className="form-row">
                            <FormField label="Login" htmlFor="login">
                                <input id="login" name="login" type="text" autoComplete="new-password"
                                    placeholder="login" value={addPayload.login} onChange={handleChange} />
                            </FormField>
                            <FormField label="Senha" htmlFor="password">
                                <div className="password-field">
                                    <input id="password" name="password" type={showDepositorPassword ? "text" : "password"} autoComplete="new-password"
                                        placeholder="password" value={addPayload.password} onChange={handleChange} />
                                    <button type="button" className="toggle"
                                        aria-label={showDepositorPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                        onClick={() => setShowDepositorPassword(v => !v)}>
                                        { showDepositorPassword ? <Eye size={18} /> : <EyeClosed size={18} /> }
                                    </button>
                                </div>
                            </FormField>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default AdminModalDepost;
